import React from 'react';
import { BookModel } from '../interfaces/book.interface';
import { computeBookCompleteness } from '../helper/bookCompleteness';

interface BookCompleteBadgeProps {
  /** When provided, completeness is computed from the full book (pages map). */
  book?: BookModel | null;
  /** Precomputed summary — for server-enriched surfaces that lack the pages map. */
  isComplete?: boolean;
  reasons?: string[];
  className?: string;
  /** Label shown when the book is complete (default "Complete"). */
  completeLabel?: string;
}

/**
 * Print-readiness pill: green "Complete" when the book would print with no blanks,
 * amber with the first failing reason otherwise (full reasons in the tooltip).
 * Pass `book` to compute locally, or `isComplete`/`reasons` for server-enriched rows.
 */
const BookCompleteBadge: React.FC<BookCompleteBadgeProps> = ({
  book,
  isComplete,
  reasons,
  className = '',
  completeLabel = 'Complete',
}) => {
  let complete: boolean;
  let reasonList: string[];

  if (book !== undefined && book !== null) {
    const c = computeBookCompleteness(book);
    complete = c.isComplete;
    reasonList = c.reasons;
  } else {
    complete = !!isComplete;
    reasonList = reasons ?? [];
  }

  const label = complete ? completeLabel : reasonList[0] ?? 'Incomplete';
  const tooltip = complete ? 'All pages, images and cover present (print-ready)' : reasonList.join(', ');
  const styles = complete ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800';

  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium normal-case ${styles} ${className}`}
      title={tooltip}
    >
      {label}
    </span>
  );
};

export default BookCompleteBadge;
